import { Box, IconButton, Modal, Typography, FormControl, Select, MenuItem } from '@mui/material';
import React from 'react';
import './TeacherEnableModal.css';
import closeIcon from '../../../../../assets/Admin/RequestImages/close-icon.svg';
import approveIcon from '../../../../../assets/Admin/RequestImages/approve-icon.svg';
import DisableIcon from '../../../../../assets/Admin/RequestImages/disable-icon.svg';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { axiosInstance } from '../../../../../utils/tokenUtils';
import { useState } from 'react';
import { Checkbox, FormControlLabel } from '@mui/material';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '500px',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: '12px',
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '90vh',
  height: 'auto', // Add this to ensure proper height calculation
  overflow: 'hidden', // Add this to prevent double scrollbars
};

const customToast = message => {
  toast.custom(
    t => (
      <div className="flex bg-[#F1FFF4] shadow-lg rounded-lg mt-[40px]">
        <div className="flex-1 w-full p-4 flex items-center gap-4">
          <CheckCircleIcon style={{ color: '#7BCF8C' }} />
          <p className="text-sm font-medium text-gray-900">{message}</p>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="w-[14px] h-[14px] cursor-pointer rounded-full flex items-center justify-center border border-[#D5DEE3] p-2"
          >
            <CloseIcon style={{ color: '#D5DEE3', width: '14px', height: '14px' }} />
          </button>
        </div>
      </div>
    ),
    {
      duration: 3000,
      position: 'top-right',
    }
  );
};

const DISABLING_REASONS = [
  'Inappropriate behavior',
  'Account inactive',
  'Multiple policy violations',
  'Suspicious activity',
  'User requested',
  'Platform policy violation',
  'Temporary restriction',
  'Security concerns',
];

const getFeatureDisplayName = feature => {
  switch (feature) {
    case 'wallet':
      return 'Wallet Access';
    case 'chat':
      return 'Chat Access';
    case 'booking':
      return 'Booking Access';
    default:
      return 'Account';
  }
};

const TeacherEnableModal = ({
  setOpenApproveModal,
  rowId,
  teacherName,
  isActive,
  featureToToggle,
  isEnabling,
  currentFeatures,
}) => {
  const navigate = useNavigate();
  const [reason, setReason] = useState('');
  const [selectedPresetReason, setSelectedPresetReason] = useState('');
  const [error, setError] = useState('');
  // Local permission state when enabling/disabling a user account
  const [walletAccess, setWalletAccess] = useState(currentFeatures?.canUseWallet ?? true);
  const [chatAccess, setChatAccess] = useState(currentFeatures?.canUseChat ?? true);
  const [bookingAccess, setBookingAccess] = useState(currentFeatures?.canUseBookings ?? true);

  const isDisablingFeature = featureToToggle && !isEnabling;
  const isEnablingFeature = featureToToggle && isEnabling;
  const isDisablingAccount = isActive === true && !featureToToggle;
  const isEnablingAccount = isActive === false && !featureToToggle;

  const getModalTitle = () => {
    if (isDisablingFeature) {
      return `Disable ${getFeatureDisplayName(featureToToggle)}`;
    } else if (isEnablingFeature) {
      return `Enable ${getFeatureDisplayName(featureToToggle)}`;
    } else if (isDisablingAccount) {
      return 'Disable User';
    } else {
      return 'Enable User';
    }
  };

  const getActionText = () => {
    if (isDisablingFeature) {
      return `disable ${getFeatureDisplayName(featureToToggle).toLowerCase()}`;
    } else if (isEnablingFeature) {
      return `enable ${getFeatureDisplayName(featureToToggle).toLowerCase()}`;
    } else if (isDisablingAccount) {
      return 'disable this user';
    } else {
      return 'enable this user';
    }
  };

  const handleClose = () => {
    setOpenApproveModal(false);
  };

  const handlePresetReasonChange = event => {
    const selectedReason = event.target.value;
    setSelectedPresetReason(selectedReason);
    if (selectedReason) {
      setReason(selectedReason);
      setError('');
    }
  };

  const handleTextChange = e => {
    setReason(e.target.value);
    if (e.target.value !== selectedPresetReason) {
      setSelectedPresetReason('');
    }
    setError('');
  };

  const handleApprove = async () => {
    try {
      const needsReason = isDisablingFeature || isDisablingAccount;
      if (needsReason && !reason.trim()) {
        setError('Please provide a reason');
        return;
      }

      // Create base payload with reason if needed
      let payload = {};
      if (needsReason) {
        payload.reason_for_disable = reason.trim();
      }

      // Determine the action value for the endpoint
      let actionValue;

      // If we're toggling a specific feature (not enabling/disabling the entire account)
      if (featureToToggle) {
        // Use the intended toggle state to determine if action is enable or disable
        // If we are enabling the feature, action should be 'enable', otherwise 'disable'.
        actionValue = isEnabling ? 'enable' : 'disable';

        // Set the feature toggles based on what's being changed
        if (featureToToggle === 'wallet') {
          payload.can_use_wallet = isEnabling;
          payload.can_use_chat = currentFeatures.canUseChat;
          payload.can_use_bookings = currentFeatures.canUseBookings;
        } else if (featureToToggle === 'chat') {
          payload.can_use_wallet = currentFeatures.canUseWallet;
          payload.can_use_chat = isEnabling;
          payload.can_use_bookings = currentFeatures.canUseBookings;
        } else if (featureToToggle === 'booking') {
          payload.can_use_wallet = currentFeatures.canUseWallet;
          payload.can_use_chat = currentFeatures.canUseChat;
          payload.can_use_bookings = isEnabling;
        }
      } else {
        // For account enabling/disabling
        actionValue = isEnablingAccount ? 'enable' : 'disable';

        // Include the selected permissions from the checkboxes
        payload.can_use_wallet = walletAccess;
        payload.can_use_chat = chatAccess;
        payload.can_use_bookings = bookingAccess;
      }

      // Use the same endpoint structure for all actions
      const endpoint = `/admin/teachers/action/${actionValue}/${rowId}`;
      const res = await axiosInstance.post(endpoint, payload);

      if (res?.status === 200) {
        setOpenApproveModal(false);

        // Refresh the page to show updated status
        window.location.reload();

        // Show success message
        let successMessage = '';
        if (featureToToggle) {
          successMessage = `${teacherName}'s ${getFeatureDisplayName(featureToToggle).toLowerCase()} ${isEnabling ? 'enabled' : 'disabled'}`;
        } else {
          successMessage = `${teacherName}'s account ${isEnablingAccount ? 'enabled' : 'disabled'}`;
        }
        customToast(successMessage);
      }
    } catch (error) {
      console.log(error);
      // Extract error message properly from the error response
      let errorMessage = 'An error occurred';
      if (error.response?.data?.detail) {
        // Handle if detail is an array of validation errors
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail[0]?.msg || 'Validation error';
        } else {
          // Handle if detail is a string
          errorMessage = error.response.data.detail;
        }
      }
      toast.error(errorMessage);
    }
  };

  const getButtonText = () => {
    if (isDisablingFeature) {
      return 'Disable';
    } else if (isEnablingFeature) {
      return 'Enable';
    } else if (isDisablingAccount) {
      return 'Disable';
    } else {
      return 'Enable';
    }
  };

  const getButtonColor = () => {
    if (isDisablingFeature || isDisablingAccount) {
      return 'var(--Acc5, #e14949)';
    } else {
      return 'var(--Acc3, #0a5ef3)';
    }
  };

  return (
    <Modal
      open={true}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Box sx={style}>
        {/* Fixed Header */}
        <Box className="flex items-center justify-between px-6 py-4 border-b border-[#D5DEE3]">
          <Typography className="modal-title-text">{getModalTitle()}</Typography>
          <IconButton onClick={handleClose}>
            <img src={closeIcon} alt="close" />
          </IconButton>
        </Box>

        {/* Scrollable Content */}
        <Box className="modal-scrollable-content">
          <Box className="flex flex-col items-center justify-center text-center gap-4 px-[8%] py-6">
            <img
              src={isDisablingFeature || isDisablingAccount ? DisableIcon : approveIcon}
              alt={isDisablingFeature || isDisablingAccount ? 'disable' : 'enable'}
              style={{ height: '120px', width: '400px' }}
            />
            <Typography className="modal-message-text">
              Are you sure you want to {getActionText()}?
              {(isDisablingFeature || isDisablingAccount) && ' This action requires a reason.'}
            </Typography>

            {/* Permission checkboxes - visible when enabling/disabling the whole account */}
            {!featureToToggle && isActive === true && (
              <Box className="flex flex-row gap-2 items-start w-[100%]">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={walletAccess}
                      onChange={e => setWalletAccess(e.target.checked)}
                      sx={{ color: '#0A5EF3', '&.Mui-checked': { color: '#0A5EF3' } }}
                    />
                  }
                  label="Wallet Access"
                  sx={{
                    '& .MuiFormControlLabel-label': { fontFamily: 'Poppins', fontSize: '14px' },
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={chatAccess}
                      onChange={e => setChatAccess(e.target.checked)}
                      sx={{ color: '#0A5EF3', '&.Mui-checked': { color: '#0A5EF3' } }}
                    />
                  }
                  label="Chat Access"
                  sx={{
                    '& .MuiFormControlLabel-label': { fontFamily: 'Poppins', fontSize: '14px' },
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={bookingAccess}
                      onChange={e => setBookingAccess(e.target.checked)}
                      sx={{ color: '#0A5EF3', '&.Mui-checked': { color: '#0A5EF3' } }}
                    />
                  }
                  label="Booking Access"
                  sx={{
                    '& .MuiFormControlLabel-label': { fontFamily: 'Poppins', fontSize: '14px' },
                  }}
                />
              </Box>
            )}

            {(isDisablingFeature || isDisablingAccount) && (
              <Box className="flex flex-col justify-start items-start gap-4 w-[100%]">
                {/* Preset Reasons Dropdown */}
                <Box className="flex flex-col justify-start items-start gap-2 w-[100%]">
                  <Typography className="reason-label-text">Quick Reasons</Typography>
                  <FormControl sx={{ width: '100%' }}>
                    <Select
                      value={selectedPresetReason}
                      onChange={handlePresetReasonChange}
                      displayEmpty
                      className="preset-reason-dropdown"
                      IconComponent={KeyboardArrowDownIcon}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 200,
                            borderRadius: '8px',
                            marginTop: '4px',
                          },
                        },
                      }}
                      sx={{
                        height: '44px',
                        borderRadius: '8px',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: '1px solid #D5DEE3',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#949DA6',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#0A5EF3',
                          borderWidth: '2px',
                        },
                        '& .MuiSelect-select': {
                          padding: '10px 14px',
                          fontFamily: 'Poppins',
                          fontSize: '14px',
                          color: selectedPresetReason ? '#14171F' : '#949DA6',
                        },
                      }}
                    >
                      <MenuItem value="" disabled sx={{ color: '#949DA6', fontStyle: 'italic' }}>
                        Select a reason
                      </MenuItem>
                      {DISABLING_REASONS.map((reasonText, index) => (
                        <MenuItem
                          key={index}
                          value={reasonText}
                          sx={{
                            fontFamily: 'Poppins',
                            fontSize: '14px',
                            padding: '10px 16px',
                            '&:hover': {
                              backgroundColor: '#F0F4FF',
                            },
                            '&.Mui-selected': {
                              backgroundColor: '#E9F1FF',
                              '&:hover': {
                                backgroundColor: '#E9F1FF',
                              },
                            },
                          }}
                        >
                          {reasonText}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {/* Custom Reason Text Area */}
                <Box className="flex flex-col justify-start items-start gap-2 w-[100%]">
                  <Typography className="reason-label-text">
                    Reason Details <span className="text-red-500">*</span>
                  </Typography>
                  <textarea
                    placeholder="Write or modify the reason..."
                    className="reason-textarea"
                    value={reason}
                    onChange={handleTextChange}
                    rows={3}
                  />
                  {error && <Typography className="text-red-500 text-sm mt-1">{error}</Typography>}
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Fixed Footer */}
        <Box className="flex items-center justify-between gap-4 px-6 py-4 border-t border-[#D5DEE3] mt-auto">
          <button className="cancel-button" onClick={handleClose}>
            Cancel
          </button>
          <button
            className="action-button"
            onClick={handleApprove}
            style={{
              backgroundColor: getButtonColor(),
              borderColor: getButtonColor(),
            }}
          >
            {getButtonText()}
          </button>
        </Box>
      </Box>
    </Modal>
  );
};

export default TeacherEnableModal;
